import { useState, Fragment, useEffect, MouseEventHandler, useRef } from 'react';
import { CircularProgress, FormControlLabel, Typography, Box, IconButton, Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LayersIcon from '@mui/icons-material/Layers';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import { LineString } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import { Stack } from '@mui/system';

import { useSessionStore } from 'session/useSessionStore';
import { useSeismicStore } from '../../stores/useSeismicStore';
import { useLine3DNavigationStore } from '../../stores/useLine3DNavigationStore';
import { IIntersections } from '../../models/interfaces/IIntersection';
import { useMapStore } from '../../stores/useMapStore';
import { createStyleProjections } from '../../utils/seismicProjectionsUtils';
import { useGetAllLayersSetupProjection } from './api/useSeismicController';
import { SeismicCheckbox } from './SeismicCheckbox';
import { SurveyType } from 'features/seismic/models/enums/SurveyType';
import { useFeatureFlags } from 'features/seismic/hooks/useFeatureFlags';
import { LineType } from 'features/seismic/models/enums/LineType';
import { useGetSurveyGeometry } from '../worldMap/api/useWorldMapController';
import { getParamsFromUrl } from 'features/seismic/utils/seismicUrlUtils';
import { useGetGeometryProjections } from './hooks/useGetGeometryProjections';
import { calculateXPosition, getTrace, getXByScale, getXByTrace } from 'features/seismic/utils/calculateScreenValuesUtils';
import { FeatureType } from 'features/seismic/models/enums/FeatureType';

export function SeismicProjections() {
    const { t } = useTranslation();

    const { map } = useMapStore(state => ({
        map: state.map
    }));

    const urlParams = getParamsFromUrl();
    const srid = useSessionStore(state => state.tenantConfiguration?.miniMapSrid);

    const { surveyMetadata, scale, geometryLine:  dataGeometryLine, volumeToken, volumeIdentifier, isInverted, calculator} = useSeismicStore(state => ({
        geometryLine: state.geometryLine,
        surveyMetadata: state.surveyMetadata,
        scale: state.scale,
        volumeToken: state.volumeToken,
        volumeIdentifier: state.volumeIdentifier,
        isInverted: state.isInverted,
        calculator: state.calculator
    }));

    const { tenantConfig } = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration,
    }));

    const { lineNumber, lineType } = useLine3DNavigationStore(state => ({
        lineNumber: state.lineNumber,
        lineType: state.lineType
    }));

    const { featureFlags } = useFeatureFlags();

    const { isLoading: loadingProjections, data: dataProjections } = useGetAllLayersSetupProjection(featureFlags?.seismic2D.Projections ?? false);

    const [opened, setOpened] = useState(false);

    const [layers, setLayers] = useState<{ [token: string]: VectorLayer<VectorSource> }>({});

    const [intersections, setIntersections] = useState<{ [token: string]: IIntersections[] }>({});

    const iconButtonElementRef = useRef<HTMLButtonElement | null>(null);

    const { data: geometryLine } = useGetGeometryProjections(srid, surveyMetadata?.Type === SurveyType.Seismic2D);

    useEffect(() => {
        if (layers) {
            for (let token in layers) {
                map?.removeLayer(layers[token]);
            }

            setLayers({});
        }
    }, [lineNumber, lineType]);

    useEffect(() => {
        if (intersections){
            for (let tokenIntersection in intersections) {
                drawProjection(intersections[tokenIntersection], tokenIntersection);
            }
        }
    }, [scale, isInverted]);

    const drawProjection = (intersectionInformation: IIntersections[], token: string) => {
        setIntersections({
            ...intersections,
            [token]: intersectionInformation
        });

        if (intersectionInformation && dataGeometryLine && surveyMetadata) {
            let layer = layers[token];

            if (!layer){
                layer = new VectorLayer({ source: new VectorSource() });

                map?.addLayer(layer);
            }

            layer.getSource()?.clear();

            let item = dataProjections?.find((item) => item.token === token);

            intersectionInformation.forEach(element => {
                let startTrace = 0;
                let endTrace = 0;
                let increment = calculator?.getLineIncrement(surveyMetadata) ?? 1;
                let surveyStart = calculator?.getMinTrace(surveyMetadata) ?? 0;
                let surveyEnd = calculator?.getMaxTrace(surveyMetadata) ?? 0;
                let totalTracesSurvey = calculator?.getTotalTraces(surveyMetadata) ?? 0;
                let realStartTrace =  element.StartTrace;
                let realEndTrace = element.EndTrace;

                if (increment > 1) {
                    realStartTrace =  ((element.StartTrace)) - ((dataGeometryLine.Min - surveyStart) / increment);
                    realEndTrace = ((element.EndTrace)) - ((dataGeometryLine.Min - surveyStart) / increment);
                }

                startTrace = calculateXPosition(realStartTrace, totalTracesSurvey, surveyStart, surveyEnd, increment, dataGeometryLine.Min, isInverted, scale.x);
                endTrace = calculateXPosition(realEndTrace, totalTracesSurvey, surveyStart, surveyEnd, increment, dataGeometryLine.Min, isInverted, scale.x);

                /*if (surveyMetadata.Type === SurveyType.Seismic3D){
                    start = dataGeometryLine.Min;
                    totalTraces = dataGeometryLine.Max;
                }*/

                //startTrace = getXByTrace(element.StartTrace, scale.x, surveyStart, increment, false, totalTraces);
                //endTrace =  getXByTrace(element.EndTrace, scale.x, surveyStart, increment, false, totalTraces);

                /**** REFACTORED  ***/
                /*
                startTrace = surveyStart + (((element.StartTrace * increment) + (dataGeometryLine.Min - surveyStart)) );
                endTrace = surveyStart + (((element.EndTrace * increment) + (dataGeometryLine.Min - surveyStart)));
                if (isInverted) {
                    if (increment > 1) {
                        startTrace = (surveyEnd - startTrace) + surveyStart - (totalTracesSurvey / increment);
                        endTrace =  (surveyEnd - endTrace) + surveyStart - (totalTracesSurvey / increment);
                    } else {
                        startTrace = (surveyEnd - startTrace) + surveyStart;
                        endTrace =  (surveyEnd - endTrace) + surveyStart;
                    }
                }
                startTrace = getXByScale(startTrace, scale.x, surveyStart);
                endTrace = getXByScale(endTrace, scale.x, surveyStart);
                */

                const middle = (endTrace - startTrace) / 2 + startTrace;

                const height = surveyMetadata.Header.SamplesPerTrace * -1 * scale.y;
                const leftTop: Coordinate = [startTrace, 0];
                const leftBottom: Coordinate = [startTrace, height];
                const rightBottom: Coordinate = [endTrace, height];
                const rightTop: Coordinate = [endTrace, 0];
                const middleBottom: Coordinate = [middle, height];

                const coordinates: Coordinate[] = element.FeatureType === FeatureType.Line || element.FeatureType === FeatureType.Point ?
                    [leftBottom, leftTop]
                    :
                    [middleBottom, leftBottom, leftTop, rightTop, rightBottom, middleBottom];

                const geometry = new LineString(coordinates);
                const feature = new Feature({ geometry: geometry });
                feature.set('featureType', 'dataLayerProjection');
                feature.set('itemConfig', item);
                feature.set('featureElement', element);
                feature.set('featureGeometry', geometry);
                layer.getSource()?.addFeature(feature);

                feature.setStyle(createStyleProjections(item!, element.FeatureName, geometry));

                setLayers({ ...layers, [token]: layer });
            });
        }
    };

    const handleClickButton:MouseEventHandler<HTMLButtonElement> = (event) => {
        setOpened((value) => !value);
    };

    if (loadingProjections || !dataGeometryLine ) {
        return <CircularProgress />;
    }

    return (
        <Box>
            <Fragment>
                <IconButton
                    id='projections'
                    color='primary'
                    ref={iconButtonElementRef}
                    onClick={handleClickButton}
                    title={t('projection')}>
                    <LayersIcon />
                </IconButton>
            </Fragment>

            <Popover
                anchorEl={iconButtonElementRef.current}
                open={opened}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                keepMounted
                onClose={() => setOpened(false)}
            >
                {
                    !!srid
                    &&
                <Stack direction='column' justifyContent='flex-start' visibility={opened ? 'visible': 'hidden'} >
                    {dataProjections?.map((item, index) => (
                        <Box key={'container-projection-' + item.token}
                            style={{
                                opacity: 0.9,
                                backgroundColor: tenantConfig?.appBar.menu.bgColor,
                                color: tenantConfig?.appBar.menu.color,
                                padding: '5px'
                            }}
                        >
                            <FormControlLabel key={index}
                                style={{
                                    color: tenantConfig?.appBar.menu.color,
                                }}
                                control={
                                    <SeismicCheckbox
                                        afterGetCallback={drawProjection}
                                        dataGeometryLine={dataGeometryLine}
                                        srid={srid}
                                        token={item.token}
                                        key={item.token}
                                        volumeToken={volumeToken ?? ''}
                                    />}
                                label={<Typography color={'white'}>{item.title}</Typography>} />
                        </Box>
                    ))}
                </Stack>
                }
            </Popover>
        </Box>
    );
};